import React from 'react';
import { clsx } from 'clsx';

export type GridTableRowProps = Pick<HTMLElement, 'role'> & {
  columns: GridTableRowCell[];
  className?: string;
  /** ARIA role rendered on each column; header rows pass 'columnheader'. */
  cellRole?: 'cell' | 'columnheader';
};

export type GridTableRowCell = {
  key: string | number;
  label: string | JSX.Element;
  align: 'left' | 'center' | 'right';
  widthFactor: number; // Relative width factor
};

const getTextAlignment = (align: 'left' | 'center' | 'right') => {
  switch (align) {
    case 'left':
      return 'justify-start text-left';
    case 'center':
      return 'justify-center text-center';
    case 'right':
      return 'justify-end text-right';
    default:
      return 'justify-start text-left';
  }
};

/**
 * Builds the grid tracks from relative width factors.
 *
 * Tracks are `fr`, not `%`: `fr` distributes the space left *after* the gaps,
 * whereas percentage tracks summing to 100% leave no room for them, so the gaps
 * push the last column past the row's content box. That overflow is what left
 * the right-aligned Balance column sitting outside the padding the sum footer
 * aligns to.
 *
 * `minmax(0, …)` keeps the tracks strictly proportional -- a bare `Nfr` floors
 * at min-content, so a long, unbreakable cell would steal width from its
 * neighbours (and would defeat `truncate` on the account name).
 */
const calculateColumnWidths = (widthFactors: number[]) =>
  widthFactors.map((factor) => `minmax(0, ${factor}fr)`).join(' ');

export const GridTableRow: React.FC<GridTableRowProps> = ({ className, columns, role, cellRole }) => {
  const columnWidths = calculateColumnWidths(columns.map((col) => col.widthFactor));
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: columnWidths,
    gap: '4px',
  };

  return (
    <div className={clsx('GridTableRow grid', className)} style={gridStyle} role={role ?? 'row'}>
      {columns.map((column) => (
        <div
          key={column.key}
          className={`GridTableRow-cell ${getTextAlignment(column.align)} font-bold`}
          role={cellRole ?? 'cell'}
        >
          {typeof column.label === 'string' ? column.label : column.label}
        </div>
      ))}
    </div>
  );
};
